import { useCallback } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import AddNodeMenu from '../../../components/chatflow/AddNodeMenu'
import { Canvas } from '../../../components/chatflow/styles'

/**
 * FlowCanvas — Encapsulates ReactFlow and its visual plugins.
 *
 * Responsibilities:
 * - Render the canvas with Background, Controls and MiniMap
 * - Expose all interaction handlers via props
 * - Render the floating AddNodeMenu
 */
interface FlowCanvasProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  canvasRef: React.RefObject<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodes: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  edges: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNodesChange: (changes: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEdgesChange: (changes: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onConnect: (params: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onConnectEnd: (event: any, connectionState: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNodeClick: (event: any, node: any) => void;

  onPaneClick: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNodeContextMenu: (event: any, node: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPaneContextMenu: (event: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInit: (instance: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onNodesDelete: (nodes: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodeTypes: Record<string, any>;
  addNode: (type: string) => void;
}

export default function FlowCanvas({
  canvasRef,
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onConnectEnd,
  onNodeClick,
  onPaneClick,
  onNodeContextMenu,
  onPaneContextMenu,
  onInit,
  onNodesDelete,
  nodeTypes,
  addNode,
}: FlowCanvasProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const minimapNodeColor = useCallback((node: any) => {
    const colorMap: Record<string, string> = { trigger: '#25D366', step: '#3B82F6', action: '#8B5CF6' }
    return colorMap[node.type] || '#94A3B8'
  }, [])

  return (
    <Canvas ref={canvasRef}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onConnectEnd={onConnectEnd}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onNodeContextMenu={onNodeContextMenu}
        onPaneContextMenu={onPaneContextMenu}
        onInit={onInit}
        nodeTypes={nodeTypes}
        onNodesDelete={onNodesDelete}
        fitView
        fitViewOptions={{ padding: 0.4 }}
        deleteKeyCode={['Backspace', 'Delete']}
        snapToGrid
        snapGrid={[24, 24]}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ type: 'smoothstep' }}
      >
        <Background variant={'dots' as never} gap={24} size={1} color="rgba(27,42,74,0.07)" />
        <Controls position="bottom-left" showInteractive={false} className="cf-controls" />
        <MiniMap
          nodeColor={minimapNodeColor}
          maskColor="rgba(27,42,74,0.04)"
          className="cf-minimap"
          pannable
          zoomable
        />
      </ReactFlow>

      <AddNodeMenu onAdd={addNode} />
    </Canvas>
  )
}
